import { Flex, HStack, Text, Tooltip } from '@chakra-ui/react'
import { capitalizeFirstLetter, FileDBValue, UserGroup } from '@hb/shared'
import { DocumentReference } from 'firebase/firestore'
import { useCallback, useState } from 'react'
import { ActionButton, SolidActionButton } from '../Buttons'
import { Expandable } from '../Expandable'
import { CollapseHorizontal } from '../shared'
import { BoxHeader } from '../Text/BoxHeader'
import { AddFileComponent, FilesListView } from './FilesListView'

interface FilesListViewWrapperProps {
  docRef: DocumentReference
  files: Record<string, FileDBValue> | undefined
  access: UserGroup
  preview?: boolean
  adminView: boolean
  AddFileComponent?: AddFileComponent
  canMerge?: boolean
  canEdit?: boolean
  onCombineFiles: (fileIds: string[]) => Promise<void>
  getFileHeader?: (fileId: string, file: FileDBValue) => React.ReactNode
}

export const FilesListViewWrapper = ({
  docRef,
  files,
  access,
  preview,
  adminView,
  AddFileComponent,
  canMerge,
  canEdit,
  onCombineFiles,
  getFileHeader,
}: FilesListViewWrapperProps) => {
  const [selectingForMerge, setSelectingForMerge] = useState(false)
  const [selectedFileIds, setSelectedFileIds] = useState<string[] | null>(null)
  const [expanded, setExpanded] = useState(true)
  const [mergingFiles, setMergingFiles] = useState(false)

  const onCancelMerge = useCallback(() => {
    setSelectedFileIds(null)
    setSelectingForMerge(false)
  }, [])

  const onMergeStart = useCallback(() => {
    setSelectedFileIds([])
    setSelectingForMerge(true)
  }, [])

  const onMergeConfirm = useCallback(async () => {
    if (selectedFileIds?.length && selectedFileIds.length >= 2) {
      setMergingFiles(true)
      try {
        await onCombineFiles(selectedFileIds)
      } finally {
        setMergingFiles(false)
        setSelectedFileIds(null)
        setSelectingForMerge(false)
      }
    }
  }, [selectedFileIds, onCombineFiles])

  return (
    <Expandable
      borderRadius={preview ? 0 : 6}
      initExpanded
      boxShadow="md"
      bg="white"
      closeCallback={() => {
        onCancelMerge()
        setExpanded(false)
      }}
      openCallback={() => setExpanded(true)}
      header={() => (
        <BoxHeader py={1} pr={2} w="100%">
          <HStack w="100%">
            <Text fontSize={preview ? 'sm' : 'md'} pl={2}>
              {access === 'patient' ? 'Shared' : capitalizeFirstLetter(access)} Files
            </Text>
            {canMerge && expanded && !preview ? (
              <Flex ml="auto" align="center">
                <CollapseHorizontal in={!selectingForMerge} width={150}>
                  <ActionButton
                    px={3}
                    size="xs"
                    onClick={e => {
                      e.stopPropagation()
                      onMergeStart()
                    }}>
                    <Text>Combine files into PDF</Text>
                  </ActionButton>
                </CollapseHorizontal>
                {preview ? null : (
                  <CollapseHorizontal in={selectingForMerge} width={210}>
                    <ActionButton
                      size="xs"
                      colorScheme="gray"
                      isDisabled={mergingFiles}
                      onClick={e => {
                        e.stopPropagation()
                        onCancelMerge()
                      }}>
                      Cancel
                    </ActionButton>
                    <Tooltip
                      label={
                        selectedFileIds && selectedFileIds.length < 2
                          ? 'Select multiple files to merge'
                          : ''
                      }
                      hasArrow>
                      <SolidActionButton
                        ml={2}
                        size="xs"
                        isLoading={mergingFiles}
                        opacity={(selectedFileIds?.length ?? 0) > 1 ? 1 : 0.7}
                        onClick={e => {
                          e.stopPropagation()
                          onMergeConfirm()
                        }}>
                        Combine Selected
                      </SolidActionButton>
                    </Tooltip>
                  </CollapseHorizontal>
                )}
              </Flex>
            ) : null}
          </HStack>
        </BoxHeader>
      )}>
      <FilesListView
        accessLevel={access}
        stackProps={{ borderBottomRadius: 6 }}
        docRef={docRef}
        canEdit={canEdit}
        onFileSelect={
          selectingForMerge
            ? (fileId, selected) => {
                setSelectedFileIds(prev => {
                  if (selected) {
                    return prev ? [...prev, fileId] : [fileId]
                  }
                  return prev?.filter(f => f !== fileId) ?? null
                })
              }
            : undefined
        }
        mergingFileIds={selectedFileIds}
        preview={preview}
        AddFileComponent={AddFileComponent}
        mergeLoading={mergingFiles}
        files={files}
        adminView={adminView}
        getFileHeader={getFileHeader}
      />
    </Expandable>
  )
}
